export function buildWhatsAppUrl(phoneE164: string, message: string): string {
  const digits = phoneE164.replace(/\D/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export function buildFiscalShareMessage(input: {
  customerName: string;
  nfseNumber: string | null;
  workOrderNumber: string;
  sandbox: boolean;
}): string {
  const documentLabel = input.nfseNumber ? `NFS-e nº ${input.nfseNumber}` : "NFS-e";
  const sandboxNotice = input.sandbox
    ? "\n\nEste documento foi emitido em ambiente de teste e não possui validade fiscal."
    : "";
  return `Olá!\n\nSegue a ${documentLabel} referente ao serviço realizado para ${input.customerName}, vinculado à OS ${input.workOrderNumber}.${sandboxNotice}\n\nAtenciosamente,\nJA Soluções Prediais`;
}

type ShareEnvironment = {
  userAgent: string;
  mobileHint?: boolean | undefined;
  maxTouchPoints?: number | undefined;
};

export function isMobileShareEnvironment(environment?: ShareEnvironment): boolean {
  const current = environment ?? {
    userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
    mobileHint: typeof navigator === "undefined"
      ? undefined
      : (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile,
    maxTouchPoints: typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints,
  };
  if (current.mobileHint !== undefined) return current.mobileHint;
  if (/Android|iPhone|iPod|Mobile/i.test(current.userAgent)) return true;
  return /iPad|Macintosh/i.test(current.userAgent) && (current.maxTouchPoints ?? 0) > 1;
}

export function isWindowsShareEnvironment(userAgent?: string): boolean {
  const currentUserAgent = userAgent ?? (typeof navigator === "undefined" ? "" : navigator.userAgent);
  return /Windows NT/i.test(currentUserAgent);
}

function canSharePdfFile(): boolean {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function" || typeof navigator.canShare !== "function") return false;
  try {
    return navigator.canShare({ files: [new File(["pdf"], "orcamento.pdf", { type: "application/pdf" })] });
  } catch {
    return false;
  }
}

export function supportsFileShare(): boolean {
  if (!isMobileShareEnvironment()) return false;
  return canSharePdfFile();
}

export function supportsWindowsAssistedShare(): boolean {
  if (typeof navigator === "undefined" || !isWindowsShareEnvironment()) return false;
  return typeof navigator.clipboard?.writeText === "function" && canSharePdfFile();
}

export async function sharePdf(input: { blob: Blob; fileName: string; message: string; title: string }): Promise<{ messageCopied: boolean }> {
  const file = new File([input.blob], input.fileName, { type: "application/pdf" });
  if (!navigator.canShare?.({ files: [file] })) throw new Error("Compartilhamento de arquivo indisponível neste dispositivo");
  const clipboard = typeof navigator.clipboard?.writeText === "function"
    ? navigator.clipboard.writeText(input.message).then(() => true).catch(() => false)
    : Promise.resolve(false);
  const sharing = navigator.share({ title: input.title, text: input.message, files: [file] });
  const [messageCopied] = await Promise.all([clipboard, sharing]);
  return { messageCopied };
}

export function sharePdfFileOnly(input: { blob: Blob; fileName: string; title: string }): Promise<void> {
  const file = new File([input.blob], input.fileName, { type: "application/pdf" });
  if (!navigator.canShare?.({ files: [file] })) return Promise.reject(new Error("Compartilhamento de arquivo indisponível neste dispositivo"));
  return navigator.share({ title: input.title, files: [file] });
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
